/**
 * Nova's own screen when its engine cannot be reached from a browser (2026-10-29).
 *
 * The installed web app (manifest + Workbox helper, vite.config.ts) opens with no network: the
 * page comes from the service worker's copy, and this says so plainly, keeps
 * trying every few seconds, and opens Nova the moment the engine answers. The
 * desktop app does not use it: it starts and watches its own engine.
 */

import { useEffect, useState } from "react";

const RETRY_SECONDS = 5;

export function Offline({ onRetry }: { onRetry: () => Promise<unknown> }) {
  const [left, setLeft] = useState(RETRY_SECONDS);
  const [trying, setTrying] = useState(false);
  useEffect(() => {
    const id = window.setInterval(() => setLeft((n) => (n <= 1 ? RETRY_SECONDS : n - 1)), 1000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    if (left !== RETRY_SECONDS || trying) return;
    setTrying(true);
    void onRetry().finally(() => setTrying(false));
  }, [left]); // eslint-disable-line react-hooks/exhaustive-deps
  const online = typeof navigator === "undefined" || navigator.onLine;
  return (
    <div className="nv-signin-page" role="alert">
      <div className="nv-signin">
        <span className="nv-brand nv-signin-brand"><i aria-hidden="true" />Nova</span>
        <h1>Cannot connect to Nova</h1>
        <p className="nv-dim">
          {online
            ? "This device is online, but the computer running Nova is not answering. It may be switched off, Nova may be closed there, or the address may have changed."
            : "This device has no network connection. Nova opens as soon as it is back."}
        </p>
        <p className="nv-dim">{trying ? "Trying now…" : `Trying again in ${left} s.`}</p>
        <button className="nv-btn primary" disabled={trying} onClick={() => {
          setTrying(true);
          void onRetry().finally(() => { setTrying(false); setLeft(RETRY_SECONDS); });
        }}>Try now</button>
      </div>
    </div>
  );
}
